<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// Р9.3: редактирование MD-документа. Полный текст подгружается через show
// (в списке content не гоняем), сохранение с изменённым текстом запускает
// переиндексацию на бэке.
const props = defineProps({
  documentId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close', 'updateSuccess']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const i18nKey = 'ASSISTANT.KNOWLEDGE_BASES.EDIT_DOCUMENT';

const uiFlags = useMapGetter('assistantDocuments/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const isSaving = computed(() => uiFlags.value.updatingItem);

const state = reactive({
  name: '',
  content: '',
});
const originalContent = ref('');
const contentError = ref('');

onMounted(async () => {
  const document = await store.dispatch(
    'assistantDocuments/show',
    props.documentId
  );
  state.name = document?.name || '';
  state.content = document?.content || '';
  originalContent.value = state.content;
});

const handleSubmit = async () => {
  if (!state.content.trim()) {
    contentError.value = t(`${i18nKey}.CONTENT.ERROR`);
    return;
  }
  contentError.value = '';

  const contentChanged = state.content !== originalContent.value;
  try {
    await store.dispatch('assistantDocuments/update', {
      id: props.documentId,
      name: state.name,
      content: state.content,
    });
    emit('updateSuccess');
    useAlert(
      contentChanged
        ? t(`${i18nKey}.REINDEX_MESSAGE`)
        : t(`${i18nKey}.SUCCESS_MESSAGE`)
    );
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.message ||
      parseAPIErrorResponse(error) ||
      t(`${i18nKey}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleClose = () => emit('close');
const handleCancel = () => dialogRef.value.close();

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t(`${i18nKey}.TITLE`)"
    :description="$t(`${i18nKey}.DESCRIPTION`)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="2xl"
    @close="handleClose"
  >
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <form v-else class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <Input
        v-model="state.name"
        :label="$t(`${i18nKey}.NAME.LABEL`)"
        :placeholder="$t(`${i18nKey}.NAME.PLACEHOLDER`)"
      />
      <TextArea
        v-model="state.content"
        :label="$t(`${i18nKey}.CONTENT.LABEL`)"
        :placeholder="$t(`${i18nKey}.CONTENT.PLACEHOLDER`)"
        :max-length="100000"
        auto-height
        :message="contentError"
        :message-type="contentError ? 'error' : 'info'"
        class="[&_textarea]:max-h-80 [&_textarea]:font-mono [&_textarea]:text-sm"
      />
      <div class="flex gap-3 justify-between items-center w-full">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="$t('ASSISTANT.FORM.CANCEL')"
          class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
          @click="handleCancel"
        />
        <Button
          type="submit"
          :label="$t('ASSISTANT.FORM.EDIT')"
          class="w-full"
          :is-loading="isSaving"
          :disabled="isSaving"
        />
      </div>
    </form>
    <template #footer />
  </Dialog>
</template>
